import { __rest } from 'tslib';
import React, { useState, useRef, useMemo, useEffect } from 'react';
import { Network } from 'vis-network';
import { makeG2STyles, useTheme } from '@komune-io/g2-themes';
import mermaid from 'mermaid';
import { cx } from '@emotion/css';

const useStyles = makeG2STyles()({
    container: {
        width: '100%',
        height: '100%',
        outline: 'none',
        '& div': {
            outline: 'none'
        },
        '& canvas': {
            outline: 'none'
        }
    }
});
const options = {
    physics: {
        enabled: true,
        solver: 'repulsion',
        repulsion: {
            damping: 0.9,
            springConstant: 0.1,
            springLength: 300,
            nodeDistance: 150
        }
    },
    manipulation: {
        enabled: false
    },
    interaction: {
        zoomView: true
    }
};
const AutomateViewer = (props) => {
    const { transitions, onSelect, className, customNode, customEdge } = props, other = __rest(props, ["transitions", "onSelect", "className", "customNode", "customEdge"]);
    const theme = useTheme();
    const defaultStyles = useStyles();
    const [network, setNetwork] = useState(undefined);
    const containerRef = useRef(null);
    const data = useMemo(() => {
        const selfRefSize = [];
        const nodes = [];
        let hasUndefinedFrom = false;
        let nbNodes = 0;
        const edges = transitions.map((transition, index) => {
            const from = transition.from;
            const to = transition.to;
            if (from !== undefined && !selfRefSize[from])
                selfRefSize[from] = 0;
            if (from === to)
                selfRefSize[from] += 0.2;
            if (from == undefined)
                hasUndefinedFrom = true;
            nbNodes = Math.max(nbNodes, Math.max(to + 1));
            return Object.assign({ label: transition.label, id: index, from: from !== null && from !== void 0 ? from : -1, to: to, arrows: 'to', selfReference: {
                    size: 40,
                    angle: Math.PI / selfRefSize[from !== null && from !== void 0 ? from : 0],
                    renderBehindTheNode: false
                } }, (customEdge ? customEdge(transition, index) : undefined));
        });
        if (hasUndefinedFrom) {
            nodes.push({
                id: -1,
                shape: 'circle',
                color: {
                    border: theme.colors.secondary,
                    background: theme.colors.secondary,
                    highlight: {
                        border: theme.colors.secondary,
                        background: theme.colors.secondary
                    },
                    hover: {
                        background: theme.colors.secondary,
                        border: theme.colors.secondary
                    }
                },
                borderWidth: 0,
                margin: {
                    bottom: 3,
                    left: 3,
                    right: 3,
                    top: 3
                }
            });
        }
        for (let i = 0; i < nbNodes; i++) {
            nodes.push(Object.assign({ id: i, label: i.toLocaleString(), shape: 'circle', color: {
                    border: theme.colors.secondary,
                    background: theme.colors.primary + 'CC',
                    highlight: {
                        border: theme.colors.secondary,
                        background: theme.colors.primary
                    },
                    hover: {
                        background: theme.colors.primary,
                        border: theme.colors.secondary
                    }
                }, borderWidth: 2, borderWidthSelected: 3, margin: {
                    bottom: 30,
                    left: 30,
                    right: 30,
                    top: 30
                } }, (customNode ? customNode(i) : {})));
        }
        return {
            edges: edges,
            nodes: nodes
        };
    }, [transitions, customNode]);
    useEffect(() => {
        containerRef.current &&
            data &&
            setNetwork(new Network(containerRef.current, data, options));
    }, [data, containerRef.current]);
    useEffect(() => {
        if (network && onSelect) {
            network.on('click', function (properties) {
                var _a, _b;
                const edges = (_a = data === null || data === void 0 ? void 0 : data.edges) === null || _a === void 0 ? void 0 : _a.filter((edge) => properties.edges.includes(edge.id));
                const node = (_b = data === null || data === void 0 ? void 0 : data.nodes) === null || _b === void 0 ? void 0 : _b.find((node) => properties.nodes[0] === node.id);
                onSelect(edges !== null && edges !== void 0 ? edges : [], node);
            });
        }
    }, [network, onSelect]);
    return (React.createElement("div", Object.assign({}, other, { ref: containerRef, className: defaultStyles.cx(defaultStyles.classes.container, 'AruiAutomateViewer-root', className) })));
};

class S2Automate {
}
class S2InitTransition {
}
class S2Transition {
}

const S2AutomateViewer = (props) => {
    const { automate } = props, other = __rest(props, ["automate"]);
    const statesTransitions = [automate.init, ...automate.transitions];
    const transitions = useMemo(() => statesTransitions.map((stateTransition) => ({
        from: stateTransition.from
            ? (stateTransition.from.position ||
                stateTransition.from.nodePosition()) + 1
            : 0,
        to: stateTransition.to
            ? (stateTransition.to.position ||
                stateTransition.to.nodePosition()) + 1
            : 0,
        label: stateTransition.role.toString() +
            ': ' +
            stateTransition.command.toString()
    })), [automate]);
    return React.createElement(AutomateViewer, Object.assign({}, other, { transitions: transitions }));
};

const Mermaid = (props) => {
    const { chart, className } = props, other = __rest(props, ["chart", "className"]);
    mermaid.initialize({
        startOnLoad: true,
        //@ts-ignore
        theme: 'default',
        //@ts-ignore
        securityLevel: 'loose'
    });
    useEffect(() => {
        mermaid.contentLoaded();
    }, [chart]);
    return (React.createElement("div", Object.assign({ className: cx('mermaid', className) }, other), chart));
};

const SsmViewer = (props) => {
    const { automate } = props, other = __rest(props, ["automate"]);
    const transitions = useMemo(() => automate.transitions.map((transition) => {
        var _a;
        return ({
            from: (_a = transition.from) === null || _a === void 0 ? void 0 : _a.position,
            to: transition.to.position,
            label: transition.role.name + ': ' + transition.action.name
        });
    }), [automate]);
    return React.createElement(AutomateViewer, Object.assign({}, other, { transitions: transitions }));
};

export { AutomateViewer, Mermaid, S2Automate, S2AutomateViewer, S2InitTransition, S2Transition, SsmViewer };
//# sourceMappingURL=index.es.js.map
